<template>
    <ul class="nav list-group list-group-flush message-navigation">
        <li class="nav-item list-group-item message-list-itemm nav-list-header" :class="{ active: newConversation }">
            <div class="d-flex align-items-center h-100">
                <h5 class="fw-bold mb-0 ms-2 text-app-secondary">Chat</h5>
                <span>
                    <img class="ms-2 mt-1 premium-pill-img" src="../../../assets/images/premium-pill.png" />
                </span>
                <div class="ms-auto">
                    <img
                        class="cursor-pointer"
                        src="../../../assets/images/chat/search-icon.svg"
                        v-tooltip="{ title: `Search`, placement: 'left' }"
                        @click="toggleSearchBox(true)"
                    />
                    <img
                        class="mx-3 cursor-pointer"
                        src="../../../assets/images/chat/filter-bar.svg"
                        v-tooltip="{ title: `Sort by ${sortText}`, placement: 'left' }"
                        @click="toggleSort(sortBy)"
                    />
                    <img
                        class="cursor-pointer"
                        src="../../../assets/images/chat/edit-icon.svg"
                        v-tooltip="{ title: `New Chat`, placement: 'left' }"
                        @click="navigateToNewConversation"
                    />
                    <div class="inline-block ms-3">
                        <button
                            type="button"
                            class="btn font-size-xs px-1 py-0 text-primary"
                            data-bs-toggle="dropdown"
                            id="conversation-nav-ellipsis"
                        >
                            <i class="fas fa-ellipsis-h font-size-lg"></i>
                        </button>
                        <div class="dropdown-menu py-1" aria-labelledby="conversation-nav-ellipsis">
                            <a
                                class="dropdown-item font-size-sm"
                                href="javascript:void(0);"
                                id="task-manager"
                                @click="markAllAsRead"
                            >
                                <img src="../../../assets/images/chat/eye-open.svg" class="eye-open-icon" />
                                Mark All as Read
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </li>
        <li class="nav-item list-group-item message-list-item" v-if="openSearch">
            <div class="input-group">
                <input
                    type="text"
                    class="form-control border-end-0"
                    placeholder="Search User Name or Conversation Title"
                    v-model="term"
                    v-focus
                />
                <span class="input-group-append">
                    <div class="input-group-text bg-transparent px-2" @click="toggleSearchBox(false)">
                        <i class="fas fa-times dialog-close text-secondary font-size-sm"></i>
                    </div>
                </span>
            </div>
        </li>
        <li v-if="newConversation" class="nav-item list-group-item message-list-item active new-chat-li">
            <div class="d-flex align-items-center mt-1">
                <div class="ava-initials me-1">
                    <i class="fa-regular fa-comment-dots"></i>
                </div>
                <div class="ms-2 flex-grow-1">
                    <div class="d-flex align-items-start">
                        <span class="me-auto mb-1 flex-grow-1"> New Chat </span>
                        <i
                            class="fas fa-trash-alt text-danger align-self-center cursor-pointer"
                            @click="cancelNewConversation"
                            v-tooltip="{ title: `Cancel New Chat`, placement: 'left' }"
                        ></i>
                    </div>
                </div>
            </div>
        </li>
    </ul>

    <div class="conversation-list">
        <ul
            class="nav list-group list-group-flush h-100 overflow-hidden message-navigation"
            :class="{ 'custom-scrollbar': userOs == 'Windows' }"
        >
            <!-- Added extra div with v-show to make scroll pagination works
            It needs 'scroll-paginate' class when mounted -->
            <div
                v-show="(!isLoading || pageNumber > 1) && allMessageNavOptions?.length > 0"
                class="overflow-auto scroll-paginate h-100"
                :class="{ 'custom-scrollbar': userOs == 'Windows' }"
            >
                <template v-if="(!isLoading || pageNumber > 1) && allMessageNavOptions?.length > 0">
                    <li
                        v-for="(message, index) in allMessageNavOptions"
                        :key="message.id"
                        :id="`conversation-${message.id}`"
                        class="nav-item list-group-item message-list-item"
                        :class="selectedConversationClass(index)"
                        @click="handleMenuSelection(message)"
                    >
                        <div class="d-flex align-items-center min-width-0">
                            <AvaInitials
                                classes="me-2"
                                :initialsType="message?.conversationType"
                                :user="getUser(message?.loginIds[0])"
                                :showStatus="message?.isDraftConversation ? false : true"
                                :isDraftConv="message?.isDraftConversation"
                            />
                            <div class="ps-2 flex-grow-1 min-width-0">
                                <div class="d-flex align-items-center justify-content-between">
                                    <span
                                        class="mb-1 text-truncate"
                                        :class="{
                                            'fw-bold': message?.unread > 0,
                                            'text-dark': !(message?.unread > 0)
                                        }"
                                    >
                                        <span v-if="message?.title?.length > 0"> {{ message.title }} </span>
                                        <span v-else-if="message?.loginIds?.length">
                                            {{ formatConversationTitle(message.loginIds) }}
                                        </span>
                                        <span v-else-if="message?.isDraftConversation">New Chat</span>
                                        <span v-else> Just You </span>
                                    </span>
                                    <small class="text-dark" v-if="!message?.isDraftConversation">
                                        {{ formatDateToLocalBasedOnDayMode(message.modifiedOn, true) }}
                                    </small>
                                </div>
                                <div class="d-flex justify-content-between align-items-center">
                                    <div
                                        v-if="message?.isDraftConversation || message?.hasDraftMessage"
                                        class="fst-italic text-truncate me-2"
                                    >
                                        Draft
                                    </div>
                                    <div
                                        v-else
                                        class="text-truncate me-2"
                                        :class="{
                                            'fw-bold': message?.unread > 0,
                                            'text-dark': !(message?.unread > 0)
                                        }"
                                    >
                                        {{ formatDisplayMessage(sanitizeMarkup(markdownToHtml(message.text))) }}
                                    </div>
                                    <div
                                        class="d-flex align-items-center"
                                        v-if="
                                            message?.unread > 0 ||
                                            message.isMuted ||
                                            message?.isDraftConversation ||
                                            message?.hasDraftMessage
                                        "
                                    >
                                        <span
                                            v-if="message?.isImportant && message?.unread > 0"
                                            class="text-danger me-2"
                                            ><i class="fas fa-exclamation"></i
                                        ></span>
                                        <span
                                            v-if="message?.isCurrentUserTagged && message?.unread > 0"
                                            class="text-danger fw-bold pe-1 font-size-md"
                                            >@</span
                                        >
                                        <span
                                            v-if="message?.isDraftConversation || message?.hasDraftMessage"
                                            class="text-danger font-weight-bold font-size-md"
                                            ><img style="width: 1rem" src="../../../assets/images/chat/draft.svg"
                                        /></span>
                                        <b
                                            v-if="message?.unread > 0 && !message.isMuted"
                                            class="unread-count"
                                            :class="{ 'd-none': !(message?.unread > 0) }"
                                        >
                                            {{ message.unread }}
                                        </b>
                                        <span v-if="message.isMuted"
                                            ><img style="width: 1rem" src="../../../assets/images/chat/muted-bell.svg"
                                        /></span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </li>
                </template>
                <li class="py-1" v-show="isLoading && pageNumber > 1">
                    <div class="loader-parent">
                        <div class="msg-loader"></div>
                    </div>
                </li>
            </div>
            <li
                v-if="!isLoading && allMessageNavOptions?.length === 0"
                class="nav-item pe-none list-group-item text-dark h-100 d-flex justify-content-center text-center flex-column"
            >
                <h5 v-if="term">No results found</h5>

                <template v-else>
                    <h5>New Conversations will live here</h5>
                    <p>Select the New Chat icon to start a new conversation.</p>
                </template>
            </li>
            <div class="h-100 overflow-auto">
                <template v-if="isLoading && pageNumber < 2">
                    <li
                        v-for="i in 8"
                        :key="i"
                        class="nav-item list-group-item d-flex justify-content-center text-middle flex flex-column"
                    >
                        <div class="d-flex align-items-center">
                            <div class="ava-initials me-1">
                                <span></span>
                            </div>
                            <div class="ms-2 flex-grow-1">
                                <span class="shimmer-loading w-90 lighten"></span>
                                <div class="message-content">
                                    <span class="shimmer-loading w-60 lighten"></span>
                                </div>
                            </div>
                        </div>
                    </li>
                </template>
            </div>
        </ul>
    </div>
</template>

<style src="./ConversationNav.scss"></style>
<script lang="ts" src="./ConversationNav.ts"></script>
